import * as React from "react";
import { useSearch as useSearchParams } from "../../router";
import { Board, type BoardColumn } from "../board";
import { Button } from "../../button";
import { Card, CardContent } from "../../card";
import { ScopedSearchButton } from "../scoped-search-button";
import { Tabs, TabsList, TabsTrigger } from "../../tabs";
import { cn } from "../../utils";
import { INTENT_CLASSES, opportunityStageIntent } from "../../tokens";
import {
  AlertTriangle,
  Filter,
  Handshake,
  LayoutGrid,
  List as ListIcon,
} from "../../icons";
import { EmptyState } from "../empty-state";
import { IntentDot } from "../status-dot";
import { IntentBadge } from "../status-badge";
import { formatMoneyMinor } from "./format";

/**
 * PipelineSurface — the deals pipeline as ONE surface with a List | Board
 * toggle (docs/design/BOARD-REDESIGN; /styleguide/board). List and Board are two
 * projections of the SAME opportunities — a shared caption, KPI band, and
 * stage-value rail cap both; flipping the toggle re-shapes the body in place
 * (it is a view, not a separate sub-nav section). The board is a calm,
 * borderless kanban: tiles raised by surface shade (no card border), quiet
 * one-line column headers, a dynamic edge-fade that signals horizontal scroll,
 * and real drag wired to the caller's `onMove` (the `opportunities.move_stage`
 * contract). Dragging a tile across stages re-orders the list + re-weights the
 * rail, because both read one optimistic placement.
 *
 * The Board view is now the canonical `Board` primitive (UI/UX Patterns PRD
 * Issue 11) — PipelineSurface is its deal adapter: it maps stages→columns and
 * deals→cards (via `renderCard`/`TileBody`) and owns the shared List+rail
 * placement, while the drag/optimism/column-scroll engine lives in `Board`.
 */

const STALL_DAYS = 14;

export interface PipelineSurfaceStage {
  id: number;
  slug: string;
  name: string;
  is_won: number;
  is_lost: number;
  probability_pct: number;
}

export interface PipelineSurfaceDeal {
  id: number;
  uid: string;
  name: string;
  account: string | null;
  valueMinor: number;
  currency: string;
  /** Deal-level probability; falls back to the stage default when null. */
  prob: number | null;
  owner: string | null;
  stageId: number;
  /** Days since the deal last moved (from `updated_at`) — drives the stall flag. */
  ageDays: number;
}

export interface PipelineSurfaceProps {
  stages: PipelineSurfaceStage[];
  deals: PipelineSurfaceDeal[];
  /** Persist a stage move (the `opportunities.move_stage` contract). */
  onMove: (dealId: number, toStageId: number) => void | Promise<void>;
  /** Open a deal's dossier (`/o/<uid>`). */
  onOpen?: (uid: string) => void;
  /** Caption title — usually the selected pipeline's name. */
  title?: string;
  /** Primary action rendered in the caption (e.g. a "New deal" link/button). */
  createSlot?: React.ReactNode;
}

type View = "list" | "board";
type Placement = Record<number, number>;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

export function PipelineSurface({
  stages,
  deals,
  onMove,
  onOpen,
  title = "Pipeline",
  createSlot,
}: PipelineSurfaceProps) {
  const [view, setView] = React.useState<View>("board");
  // Scoped-search model (Issue 19): the dock owns the query; the board mirrors it
  // from the URL `?q=` and filters its deals in place. The caption's
  // magnifying-glass button focuses the dock (pre-scoped to the page).
  const searchParams = useSearchParams();
  const query = searchParams.get("q") ?? "";
  // Optimistic placement (dealId → stageId), shared by List + Board + rail so a
  // drag updates every projection at once; reverts on a failed persist.
  const [placement, setPlacement] = React.useState<Placement>(() =>
    Object.fromEntries(deals.map((d) => [d.id, d.stageId])),
  );
  // Reconcile when the server set changes (new deals, external moves).
  const fingerprint = deals.map((d) => `${d.id}:${d.stageId}`).join(",");
  React.useEffect(() => {
    setPlacement(Object.fromEntries(deals.map((d) => [d.id, d.stageId])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  const stageById = React.useMemo(
    () => new Map(stages.map((s) => [s.id, s])),
    [stages],
  );
  const stageRank = React.useMemo(
    () => new Map(stages.map((s, i) => [s.id, i])),
    [stages],
  );

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return deals;
    return deals.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.account?.toLowerCase().includes(q) ?? false),
    );
  }, [deals, query]);

  const stageOf = React.useCallback(
    (d: PipelineSurfaceDeal) => placement[d.id] ?? d.stageId,
    [placement],
  );
  const resolvedProb = React.useCallback(
    (d: PipelineSurfaceDeal) =>
      d.prob ?? stageById.get(stageOf(d))?.probability_pct ?? 0,
    [stageById, stageOf],
  );

  const dealsInStage = React.useCallback(
    (stageId: number) => visible.filter((d) => stageOf(d) === stageId),
    [visible, stageOf],
  );
  const stageValue = React.useCallback(
    (stageId: number) =>
      dealsInStage(stageId).reduce((n, d) => n + d.valueMinor, 0),
    [dealsInStage],
  );

  const totals = React.useMemo(() => {
    const open = visible.reduce((n, d) => n + d.valueMinor, 0);
    const weighted = visible.reduce(
      (n, d) => n + Math.round((d.valueMinor * resolvedProb(d)) / 100),
      0,
    );
    return {
      open,
      weighted,
      count: visible.length,
      avg: visible.length ? Math.round(open / visible.length) : 0,
    };
  }, [visible, resolvedProb]);

  const currency = deals[0]?.currency ?? "USD";

  const move = React.useCallback(
    async (dealId: number, toStageId: number) => {
      const prev = placement[dealId];
      if (prev === toStageId) return;
      setPlacement((p) => ({ ...p, [dealId]: toStageId }));
      try {
        await onMove(dealId, toStageId);
      } catch {
        setPlacement((p) => {
          if (prev === undefined) {
            const { [dealId]: _drop, ...rest } = p;
            return rest;
          }
          return { ...p, [dealId]: prev };
        });
      }
    },
    [placement, onMove],
  );

  const boardColumns: BoardColumn<PipelineSurfaceDeal>[] = React.useMemo(
    () =>
      stages.map((s) => ({
        id: s.id,
        name: s.name,
        intent: opportunityStageIntent(s),
        items: dealsInStage(s.id),
        badge: formatMoneyMinor(stageValue(s.id), currency),
      })),
    [stages, dealsInStage, stageValue, currency],
  );

  return (
    <Card className="overflow-hidden p-0">
      <CardContent className="p-0">
        {/* Title row — closed off with its own hairline. */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="flex items-baseline gap-2">
            <span className="type-body font-medium text-foreground">{title}</span>
            <span className="type-subtext tabular-nums text-muted-foreground">
              {totals.count} open {totals.count === 1 ? "deal" : "deals"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ScopedSearchButton label="Search deals" />
            {createSlot}
          </div>
        </div>

        {/* Metrics — KPIs + the stage-value rail (shared by both views). */}
        <SummaryBand
          stages={stages}
          open={totals.open}
          weighted={totals.weighted}
          count={totals.count}
          avg={totals.avg}
          currency={currency}
          stageValue={stageValue}
        />

        {/* The integration seam: List | Board lives on the surface itself. */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
          <Tabs value={view} onValueChange={(v) => setView(v as View)}>
            <TabsList>
              <TabsTrigger value="list">
                <ListIcon className="size-3.5" />
                List
              </TabsTrigger>
              <TabsTrigger value="board">
                <LayoutGrid className="size-3.5" />
                Board
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="sm" disabled>
            <Filter className="size-3.5" />
            Filter
          </Button>
        </div>

        {view === "list" ? (
          <DealList
            deals={visible}
            stageById={stageById}
            stageRank={stageRank}
            stageOf={stageOf}
            resolvedProb={resolvedProb}
            onOpen={onOpen}
          />
        ) : (
          <Board<PipelineSurfaceDeal>
            columns={boardColumns}
            getItemId={(d) => d.id}
            onMove={(dealId, toStageId) =>
              move(Number(dealId), Number(toStageId))
            }
            onOpen={onOpen ? (d) => onOpen(d.uid) : undefined}
            renderCard={(d) => {
              const stage = stageById.get(stageOf(d));
              const stalled =
                d.ageDays >= STALL_DAYS && !(stage?.is_won || stage?.is_lost);
              return (
                <TileBody deal={d} prob={resolvedProb(d)} stalled={stalled} />
              );
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Summary band (shared) ─────────────────────────────────────────────────

function SummaryBand({
  stages,
  open,
  weighted,
  count,
  avg,
  currency,
  stageValue,
}: {
  stages: PipelineSurfaceStage[];
  open: number;
  weighted: number;
  count: number;
  avg: number;
  currency: string;
  stageValue: (stageId: number) => number;
}) {
  const kpis = [
    { label: "Open pipeline", value: formatMoneyMinor(open, currency) },
    { label: "Weighted forecast", value: formatMoneyMinor(weighted, currency) },
    { label: "Open deals", value: String(count) },
    { label: "Avg. deal size", value: formatMoneyMinor(avg, currency) },
  ];
  return (
    <div className="border-b border-border">
      <div className="grid grid-cols-2 divide-x divide-border border-b border-border sm:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="px-4 py-2.5">
            <div className="type-subtext text-muted-foreground">{k.label}</div>
            <div className="mt-0.5 type-body font-medium tabular-nums text-foreground">
              {k.value}
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 py-2.5">
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
          {stages.map((s) => {
            const v = stageValue(s.id);
            const pct = open > 0 ? (v / open) * 100 : 0;
            if (pct <= 0) return null;
            return (
              <div
                key={s.id}
                className={cn(
                  "h-full transition-[width] duration-300",
                  INTENT_CLASSES[opportunityStageIntent(s)].dot,
                )}
                style={{ width: `${pct}%` }}
                title={`${s.name} · ${formatMoneyMinor(v, currency)}`}
              />
            );
          })}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          {stages.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1.5 type-subtext text-muted-foreground"
            >
              <IntentDot intent={opportunityStageIntent(s)} size="xs" />
              {s.name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── List (same deals, ordered by stage) ────────────────────────────────────

function DealList({
  deals,
  stageById,
  stageRank,
  stageOf,
  resolvedProb,
  onOpen,
}: {
  deals: PipelineSurfaceDeal[];
  stageById: Map<number, PipelineSurfaceStage>;
  stageRank: Map<number, number>;
  stageOf: (d: PipelineSurfaceDeal) => number;
  resolvedProb: (d: PipelineSurfaceDeal) => number;
  onOpen?: (uid: string) => void;
}) {
  const ordered = React.useMemo(
    () =>
      [...deals].sort((a, b) => {
        const ra = stageRank.get(stageOf(a)) ?? 0;
        const rb = stageRank.get(stageOf(b)) ?? 0;
        return ra - rb || b.valueMinor - a.valueMinor;
      }),
    [deals, stageRank, stageOf],
  );
  if (ordered.length === 0) {
    return (
      <EmptyState
        variant="inline"
        className="flex min-h-52 items-center justify-center"
        icon={<Handshake className="size-6" />}
        title="No open deals yet"
        description="Deals show up here as opportunities enter the pipeline. Add a lead or create an opportunity to start tracking it."
      />
    );
  }
  return (
    <div className="flex flex-col" data-slot="deal-list">
      {ordered.map((d) => {
        const stage = stageById.get(stageOf(d));
        const stalled =
          d.ageDays >= STALL_DAYS && !(stage?.is_won || stage?.is_lost);
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onOpen?.(d.uid)}
            className={cn(
              "flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left type-body",
              "transition-colors last:border-b-0 even:bg-foreground/[0.015] hover:bg-secondary",
            )}
          >
            {d.owner ? <Avatar name={d.owner} /> : <span className="size-5 shrink-0" />}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-foreground">{d.name}</span>
              {d.account ? (
                <span className="block truncate type-subtext text-muted-foreground">
                  {d.account}
                </span>
              ) : null}
            </span>
            {stalled ? (
              <span className="hidden items-center gap-1 type-subtext text-warning-fg sm:inline-flex">
                <AlertTriangle className="size-3.5" />
                {d.ageDays}d
              </span>
            ) : null}
            {stage ? (
              <IntentBadge intent={opportunityStageIntent(stage)}>
                {stage.name}
              </IntentBadge>
            ) : null}
            <span className="hidden w-14 text-right type-subtext tabular-nums text-muted-foreground sm:block">
              {resolvedProb(d)}%
            </span>
            <span className="w-24 shrink-0 text-right font-medium tabular-nums text-foreground">
              {formatMoneyMinor(d.valueMinor, d.currency)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Tile inner content — shared by the resting tile + the drag overlay. */
function TileBody({
  deal,
  prob,
  stalled,
}: {
  deal: PipelineSurfaceDeal;
  prob: number;
  stalled: boolean;
}) {
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 flex-1 truncate type-body text-foreground">
          {deal.name}
        </span>
        <span className="shrink-0 type-body font-medium tabular-nums text-foreground">
          {formatMoneyMinor(deal.valueMinor, deal.currency)}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="min-w-0 flex-1 truncate type-subtext text-muted-foreground">
          {deal.account ? `${deal.account} · ` : ""}
          {prob}%
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {stalled ? (
            <span
              className="inline-flex items-center gap-1 type-subtext tabular-nums text-warning-fg"
              title={`Stalled ${deal.ageDays} days`}
            >
              <AlertTriangle className="size-3" />
              {deal.ageDays}d
            </span>
          ) : null}
          {deal.owner ? <Avatar name={deal.owner} /> : null}
        </span>
      </div>
    </>
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-5 type-subtext font-medium text-muted-foreground"
      title={name}
    >
      {initials(name)}
    </span>
  );
}
